import type { SessionsListResult } from "../types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import { isSubagentSessionKey, parseAgentSessionKey } from "../../../../src/routing/session-key.js";
import { resolveChatTabLabel } from "./chat-tabs.ts";

export type ChatTabLabelHint = {
  label: string;
  kind: "main" | "fork" | "subagent";
};

export function buildChatTabLabelHints(
  subagents: ChatSubagentCard[] | undefined,
  sessions: SessionsListResult | null,
): Record<string, ChatTabLabelHint> {
  const hints: Record<string, ChatTabLabelHint> = {};
  for (const card of subagents ?? []) {
    const key = card.childSessionKey?.trim();
    if (!key) {
      continue;
    }
    hints[key] = {
      label: card.label?.trim() || formatSubagentTabLabel(key),
      kind: "subagent",
    };
  }
  for (const row of sessions?.sessions ?? []) {
    const key = row.key?.trim();
    if (!key || hints[key]) {
      continue;
    }
    if (isSubagentSessionKey(key)) {
      hints[key] = {
        label: row.label?.trim() || row.displayName?.trim() || formatSubagentTabLabel(key),
        kind: "subagent",
      };
    }
  }
  return hints;
}

function formatSubagentTabLabel(key: string): string {
  const parsed = parseAgentSessionKey(key);
  if (!parsed) {
    return "Subagent";
  }
  const agent = parsed.agentId.replace(/[^a-zA-Z0-9]+/g, " ").trim() || "agent";
  const agentTitle = agent.charAt(0).toUpperCase() + agent.slice(1);
  return `${agentTitle} · subagent`;
}

export function resolveChatTabLabelWithHints(
  key: string,
  tabIndex: number,
  sessions: SessionsListResult | null,
  hints?: Record<string, ChatTabLabelHint>,
  stickyLabels?: Record<string, string>,
): string {
  const hint = hints?.[key];
  if (hint?.label) {
    return hint.label;
  }
  if (isSubagentSessionKey(key)) {
    return formatSubagentTabLabel(key);
  }
  return resolveChatTabLabel(key, tabIndex, sessions, stickyLabels?.[key]);
}

export function resolveChatTabKind(
  key: string,
  hints?: Record<string, ChatTabLabelHint>,
): ChatTabLabelHint["kind"] | null {
  const hint = hints?.[key];
  if (hint?.kind) {
    return hint.kind;
  }
  if (isSubagentSessionKey(key)) {
    return "subagent";
  }
  const parsed = parseAgentSessionKey(key);
  if (parsed?.rest?.startsWith("fork:")) {
    return "fork";
  }
  if (parsed?.rest === "main" || key === "main") {
    return "main";
  }
  return null;
}
